import React from 'react';

import { gql } from '@apollo/client';
import { Typography } from '@mui/material';
import { PermissionRoleMenu } from 'src/components';
import { ToggleSwitch } from 'src/components/generic';
import { Apollo } from 'src/gql/generated';
import { useCheckWorkflowAuthorization, useGetWorkflowForwardSettings } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

void gql`
  mutation UpdateWorkflowForwarding($input: UpdateWorkflowInput!) {
    updateWorkflow(input: $input) {
      workflow {
        __typename
        id
        forwardingPermissionRole
      }
    }
  }
`;

const useStyles = makeStyles()(theme => ({
  forwardingDetails: { display: 'flex', flexDirection: 'column' },
  permissionRoleMenu: {
    height: 32,
    marginLeft: theme.spacing(1),
    width: 'fit-content',
  },
  row: {
    alignItems: 'center',
    display: 'flex',
  },
  text: {
    marginLeft: theme.spacing(2),
  },
}));

interface WorkflowForwardingToggleProps {
  workflowId: UUID;
}

export function WorkflowForwardingToggle({
  workflowId,
}: WorkflowForwardingToggleProps): JSX.Element {
  const { classes } = useStyles();
  const { workflow } = useGetWorkflowForwardSettings(workflowId, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const [updateWorkflow] = Apollo.useUpdateWorkflowForwardingMutation();
  const { canEditWorkflow } = useCheckWorkflowAuthorization(workflowId);

  async function handleToggle(forwardingPermissionRole: Apollo.PermissionRole | null) {
    await updateWorkflow({
      optimisticResponse: {
        __typename: 'Mutation',
        updateWorkflow: {
          __typename: 'WorkflowResponse',
          workflow: {
            __typename: 'Workflow',
            forwardingPermissionRole,
            id: workflowId,
          },
        },
      },
      variables: {
        input: {
          props: { forwardingPermissionRole },
          workflowId,
        },
      },
    });
  }

  return (
    <div className={classes.row}>
      <ToggleSwitch
        disabled={!canEditWorkflow}
        toggle={async () =>
          await handleToggle(
            !workflow?.forwardingPermissionRole ? Apollo.PermissionRole.Commenter : null
          )
        }
        toggled={Boolean(workflow?.forwardingPermissionRole)}
      />
      <div className={classes.forwardingDetails}>
        <PermissionRoleMenu
          MenuProps={{
            anchorOrigin: { horizontal: 'left', vertical: 'top' },
            transformOrigin: { horizontal: 'left', vertical: 'top' },
            variant: 'menu',
          }}
          border={false}
          className={classes.permissionRoleMenu}
          disabled={!workflow?.forwardingPermissionRole || !canEditWorkflow}
          handleSelect={async role => await handleToggle(role)}
          value={workflow?.forwardingPermissionRole ?? null}
        />
        <Typography className={classes.text}>
          {workflow?.forwardingPermissionRole
            ? `Recipients can add ${getForwardingRoleText(
                workflow.forwardingPermissionRole
              ).toLowerCase()}.`
            : `Workflow forwarding disabled.`}
        </Typography>
      </div>
    </div>
  );
}

function getForwardingRoleText(role: Apollo.PermissionRole): string {
  switch (role) {
    case Apollo.PermissionRole.Commenter:
      return 'commenters';
    case Apollo.PermissionRole.Suggester:
      return 'suggesters';
    case Apollo.PermissionRole.Editor:
      return 'editors';
    case Apollo.PermissionRole.Viewer:
      return 'viewers';
  }
}
